import { useEffect, useState } from "react";
import type { LatLngTuple } from "../../core/options";
import { formatEov, wgs84ToEov } from "../../geo/eov";
import { pairText } from "../../geo/geodesy";
import { useCoarsePointer, useLabels, useMapCore, useMapStore } from "../hooks";
import { copyText, machineCoordinates } from "../fieldValues";
import { Button } from "../primitives/Button";
import { Segmented } from "../primitives/Segmented";

export type CoordinateSystem = "wgs84" | "eov";

export function coordinateReadoutText(point: LatLngTuple, system: CoordinateSystem): string {
  if (system === "eov") {
    const eov = wgs84ToEov(point[0], point[1]);

    return formatEov(eov.y, eov.x);
  }

  return pairText(point);
}

export function coordinateMachineText(point: LatLngTuple, system: CoordinateSystem): string {
  if (system === "eov") {
    const eov = wgs84ToEov(point[0], point[1]);

    return `${eov.y.toFixed(2)} ${eov.x.toFixed(2)}`;
  }

  return machineCoordinates(point[0], point[1]);
}

export type CoordinateReadoutProps = { className?: string };

const copiedDelayMs = 2000;

export function CoordinateReadout({ className }: CoordinateReadoutProps) {
  const core = useMapCore();
  const labels = useLabels();
  const cursor = useMapStore(a => a.cursor);
  const centre = useMapStore(a => a.view.center);
  const system = useMapStore(a => a.coordinateSystem);
  const coarse = useCoarsePointer();
  const [copied, setCopied] = useState(false);
  const point = coarse || cursor === null ? centre : cursor;
  const text = coordinateReadoutText(point, system);

  useEffect(() => {
    if (!copied) return;

    const handle = setTimeout(() => setCopied(false), copiedDelayMs);

    return () => clearTimeout(handle);
  }, [copied]);

  return (
    <div className={["fxmap-coords", "fxmap-glass", className ?? ""].filter(Boolean).join(" ")} data-fxmap="coordinates" title={coarse || cursor === null ? labels.mapCentre : labels.cursorPosition}>
      <Segmented
        label={labels.coordinatesSwitch}
        size="sm"
        value={system}
        dataFxmap="coordinates-system"
        options={[
          { value: "wgs84", label: labels.coordinatesWgs, action: "system-wgs84" },
          { value: "eov", label: labels.coordinatesEov, action: "system-eov" }
        ]}
        onChange={next => core.setCoordinateSystem(next)}
      />
      <span className="fxmap-coords-value fxmap-mono" data-fxmap="coordinates-value">
        {text}
      </span>
      {system === "eov" ? <span className="fxmap-coords-note fxmap-muted">{labels.coordinatesApproximate}</span> : null}
      <Button kind="icon" size="sm" icon="copy" label={labels.copyMachine} className="fxmap-coords-copy" action="copy" onClick={() => void copyText(coordinateMachineText(point, system)).then(done => setCopied(done))} />
      <span className="fxmap-coords-copied" role="status" aria-live="polite">
        {copied ? labels.copied : ""}
      </span>
    </div>
  );
}
